import { useEffect, useState } from 'react';
import { Alert, Button, Card, Checkbox, FileUpload, Input, Skeleton } from '@dreamtree-org/twreact-ui';
import { ImageUp } from 'lucide-react';
import { describeUploadLink, sendToUploadLink } from '@/lib/uploads';
import { MAX_ALT } from '../lib/photos';
import {
  tokenFromHash, stripHash, headingFor, typeNames, acceptFor, sizeText, contentTypeFor, doneText,
  EXPIRED_TEXT, GENUINE_PHOTO_TEXT, GENUINE_PHOTO_CONFIRM,
} from '../lib/uploadLink';

const timeOf = (iso) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * The upload page behind an assistant's upload link (MERCH-78, D92): /admin/upload#<token>.
 * An assistant never sends image data in a tool call, so for an image it cannot attach (one it
 * generated) it gives the person this page: they see what the upload is for, pick the file, add
 * alt text when the kind needs it (and confirm a unit photo is genuine), and the browser PUTs the
 * raw file to the link. Public (the link is the credential; no sign-in, no cookies), single use,
 * valid about 30 minutes. The token is read once and taken out of the address bar.
 */
export default function UploadLink({ store }) {
  const [token] = useState(() => tokenFromHash(window.location.hash));
  const [info, setInfo] = useState(null);
  const [gone, setGone] = useState(!token);
  const [loadError, setLoadError] = useState('');
  const [done, setDone] = useState(null);
  const storeName = (store && store.name) || 'MerchantPro';

  useEffect(() => {
    stripHash();
    if (!token) return;
    describeUploadLink(token)
      .then(setInfo)
      .catch((ex) => (ex.status === 404 ? setGone(true) : setLoadError(ex.message)));
  }, [token]);

  let body;
  if (gone) body = <Alert variant="error" title="This upload link can't be used">{EXPIRED_TEXT}</Alert>;
  else if (loadError) body = <Alert variant="error">{loadError}</Alert>;
  else if (!info) body = <Skeleton count={3} />;
  else if (done) body = <Alert variant="success" title={doneText(done.review_status || info.review_status)}>You can close this page and go back to your assistant.</Alert>;
  else body = <UploadForm token={token} info={info} onDone={setDone} onGone={() => setGone(true)} />;

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-4 pt-safe pb-safe bg-[var(--app-bg)]">
      <Card className="w-full max-w-md p-6 space-y-5">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:bg-primary-950">
            <ImageUp size={24} aria-hidden />
          </div>
          <h1 className="text-xl font-bold text-primary-600">{storeName}</h1>
          <p className="text-sm font-medium">Upload an image</p>
        </div>
        {body}
      </Card>
    </div>
  );
}

/** One file, its alt text and (for a unit photo) the genuine-photo confirmation; the server checks each again. */
function UploadForm({ token, info, onDone, onGone }) {
  const [files, setFiles] = useState([]);
  const [alt, setAlt] = useState('');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const altMissing = Boolean(info.alt_required && !alt.trim());
  const attestMissing = Boolean(info.attest_required && !attested);

  const send = async () => {
    const file = files[0];
    if (!file || altMissing || attestMissing) return;
    setErr('');
    setBusy(true);
    try {
      onDone(await sendToUploadLink(token, file, { contentType: contentTypeFor(file, info.accepted_mimes), alt: alt.trim() || undefined, attested: info.attest_required && attested }));
    } catch (ex) {
      // 404: the link is gone. Anything else (413 too large, 415/422 wrong type or missing alt, 429) is the
      // server's own sentence; nothing was stored, so the same link takes a corrected file.
      if (ex.status === 404) onGone();
      else setErr(ex.message || 'The upload failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1 text-center">
        <p className="text-base font-semibold">{headingFor(info)}</p>
        <p className="text-sm opacity-70">
          {typeNames(info.accepted_mimes)}, up to {sizeText(info.max_bytes)}. One file. This link works until {timeOf(info.expires_at)}.
        </p>
      </div>
      {info.attest_required ? <Alert variant="warning" title="Genuine photos only">{GENUINE_PHOTO_TEXT}</Alert> : null}
      <FileUpload
        accept={acceptFor(info.accepted_mimes)}
        maxSize={info.max_bytes}
        capture={false}
        files={files}
        onFileSelect={(f) => setFiles(f.slice(0, 1))}
        onFileRemove={() => setFiles([])}
        disabled={busy}
      />
      <Input
        label={info.alt_required ? 'What the image shows (alt text)' : 'What the image shows (optional)'}
        required={info.alt_required}
        maxLength={MAX_ALT}
        value={alt}
        onChange={(e) => setAlt(e.target.value)}
        error={files.length && altMissing ? 'Describe what the image shows.' : undefined}
      />
      {info.attest_required ? (
        <Checkbox label={GENUINE_PHOTO_CONFIRM} checked={attested} required onChange={(e) => setAttested(e.target.checked)} />
      ) : null}
      {err ? <Alert variant="error">{err}</Alert> : null}
      <Button fullWidth loading={busy} disabled={!files.length || altMissing || attestMissing} onClick={send}>Upload</Button>
    </div>
  );
}
